import { openDB } from 'idb';

const DB_NAME = 'civicode-db';
// v7 — offline queue items may now carry captured photos as File/Blob handles,
// which IndexedDB structured-clones. No new store; the payload just got heavier,
// which is why callers check headroom via `hasQueueHeadroom` first.
const DB_VERSION = 7;
const OFFLINE_QUEUE_STORE = 'offline-queue';
const ADDRESSES_STORE = 'addresses';
const memoryFallback = {
  queue: [],
  addresses: new Map(),
};

const hasIndexedDB = () => typeof indexedDB !== 'undefined';

const ensureStores = (db) => {
  if (!db.objectStoreNames.contains(OFFLINE_QUEUE_STORE)) {
    db.createObjectStore(OFFLINE_QUEUE_STORE, { keyPath: 'id', autoIncrement: true });
  }
  if (!db.objectStoreNames.contains(ADDRESSES_STORE)) {
    const store = db.createObjectStore(ADDRESSES_STORE, { keyPath: 'id' });
    store.createIndex('combadd', 'combadd', { unique: false });
  }
};

const hasRequiredStores = (db) => (
  db.objectStoreNames.contains(OFFLINE_QUEUE_STORE)
  && db.objectStoreNames.contains(ADDRESSES_STORE)
);

export const initDB = async () => {
  if (!hasIndexedDB()) {
    return null;
  }

  const openAtVersion = (version) => openDB(DB_NAME, version, {
    upgrade(db) {
      ensureStores(db);
    },
  });

  try {
    return await openAtVersion(DB_VERSION);
  } catch (error) {
    if (error?.name !== 'VersionError') {
      throw error;
    }

    const db = await openDB(DB_NAME);
    if (hasRequiredStores(db)) {
      return db;
    }

    const nextVersion = db.version + 1;
    db.close();
    return openAtVersion(nextVersion);
  }
};

export const addToQueue = async (item) => {
  if (!hasIndexedDB()) {
    memoryFallback.queue.push(item);
    return item.id;
  }
  const db = await initDB();
  return db.add(OFFLINE_QUEUE_STORE, item);
};

export const getQueue = async () => {
  if (!hasIndexedDB()) {
    return [...memoryFallback.queue];
  }
  const db = await initDB();
  return db.getAll(OFFLINE_QUEUE_STORE);
};

export const removeFromQueue = async (id) => {
  if (!hasIndexedDB()) {
    memoryFallback.queue = memoryFallback.queue.filter((item) => item.id !== id);
    return;
  }
  const db = await initDB();
  return db.delete(OFFLINE_QUEUE_STORE, id);
};

export const updateQueueItem = async (item) => {
  if (!hasIndexedDB()) {
    memoryFallback.queue = memoryFallback.queue.map((queued) => queued.id === item.id ? item : queued);
    return item.id;
  }
  const db = await initDB();
  return db.put(OFFLINE_QUEUE_STORE, item);
};

/** Rough byte size of the photos attached to one queued item. */
export const queueItemBytes = (item) => (
  (item?.payload?.files || []).reduce((total, file) => total + (file?.size || 0), 0)
);

/**
 * Whether there is room to queue `bytes` more of captured evidence.
 *
 * A quota error thrown mid-capture would lose the photo the officer just took,
 * so callers ask first and refuse cleanly. Browsers that expose no estimate get
 * the benefit of the doubt — the write will still succeed or fail on its own.
 */
export const hasQueueHeadroom = async (bytes = 0) => {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return true;
  try {
    const { quota, usage } = await navigator.storage.estimate();
    if (!quota) return true;
    // Leave a tenth of the quota free: the service worker and cached addresses
    // share this origin's storage and must not be squeezed out by photos.
    return (usage || 0) + bytes < quota * 0.9;
  } catch (_) {
    return true;
  }
};

export const clearQueue = async () => {
  if (!hasIndexedDB()) {
    memoryFallback.queue = [];
    return;
  }
  const db = await initDB();
  return db.clear(OFFLINE_QUEUE_STORE);
};

// Address Cache Functions

export const cacheAddressList = async (addresses) => {
  if (!hasIndexedDB()) {
    addresses.forEach((addr) => memoryFallback.addresses.set(addr.id, addr));
    return;
  }
  const db = await initDB();
  const tx = db.transaction(ADDRESSES_STORE, 'readwrite');
  const store = tx.objectStore(ADDRESSES_STORE);
  await Promise.all(addresses.map(addr => store.put(addr)));
  await tx.done;
};

export const cacheAddress = async (address) => {
  if (!hasIndexedDB()) {
    memoryFallback.addresses.set(address.id, address);
    return address.id;
  }
  const db = await initDB();
  return db.put(ADDRESSES_STORE, address);
};

export const getCachedAddress = async (id) => {
  if (!hasIndexedDB()) {
    return memoryFallback.addresses.get(Number(id));
  }
  const db = await initDB();
  return db.get(ADDRESSES_STORE, Number(id)); // Ensure ID is number if needed
};

export const searchAddresses = async (query) => {
  if (!hasIndexedDB()) {
    return Array.from(memoryFallback.addresses.values()).slice(0, 20);
  }
  const db = await initDB();
  const allAddresses = await db.getAll(ADDRESSES_STORE);
  const trimmed = (query || '').trim();
  if (!trimmed) return allAddresses.slice(0, 50); // Return first 50 if no query

  const lowerQuery = trimmed.toLowerCase();
  const textFields = ['combadd', 'property_name', 'aka', 'ownername', 'owneraddress', 'name'];

  return allAddresses
    .filter((addr) => {
      if (!addr) return false;
      // Search across the same fields the API supports when online
      const haystacks = [
        ...textFields.map((key) => addr[key]),
        addr.pid,
        addr.property_id,
      ];

      return haystacks.some((field) => {
        if (field === null || field === undefined) return false;
        const value = String(field).toLowerCase();
        return value && value.includes(lowerQuery);
      });
    })
    .slice(0, 20); // Limit results
};
